import { useState, useEffect } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Clock } from "lucide-react";

interface CountdownTimerProps {
  endDate: string;
  className?: string;
  compact?: boolean;
}

const CountdownTimer = ({ endDate, className = "", compact = false }: CountdownTimerProps) => {
  const { isAr } = useLanguage();

  const calcRemaining = () => {
    const diff = new Date(endDate).getTime() - Date.now();
    if (diff <= 0) return { d: 0, h: 0, m: 0, s: 0, expired: true };
    return {
      d: Math.floor(diff / 86400000),
      h: Math.floor((diff % 86400000) / 3600000),
      m: Math.floor((diff % 3600000) / 60000),
      s: Math.floor((diff % 60000) / 1000),
      expired: false,
    };
  };

  const [time, setTime] = useState(calcRemaining);

  useEffect(() => {
    const id = setInterval(() => setTime(calcRemaining()), 1000);
    return () => clearInterval(id);
  }, [endDate]);

  if (time.expired) return null;

  const dLabel = isAr ? "يوم" : "d";
  const hLabel = isAr ? "س" : "h";
  const mLabel = isAr ? "د" : "m";
  const sLabel = isAr ? "ث" : "s";

  if (compact) {
    return (
      <div className={`inline-flex items-center gap-1.5 text-destructive font-body text-xs font-semibold ${className}`}>
        <Clock size={12}  aria-hidden="true" focusable="false" />
        <span>
          {time.d > 0 && `${time.d}${dLabel} `}
          {String(time.h).padStart(2, "0")}:{String(time.m).padStart(2, "0")}:{String(time.s).padStart(2, "0")}
        </span>
      </div>
    );
  }

  const blocks = [
    ...(time.d > 0 ? [{ value: time.d, label: isAr ? "يوم" : "Days" }] : []),
    { value: time.h, label: isAr ? "ساعة" : "Hours" },
    { value: time.m, label: isAr ? "دقيقة" : "Min" },
    { value: time.s, label: isAr ? "ثانية" : "Sec" },
  ];

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Clock size={14} className="text-destructive"  aria-hidden="true" focusable="false" />
      <span className="font-body text-xs uppercase tracking-wider text-destructive font-semibold">
        {isAr ? "ينتهي خلال" : "Ends in"}
      </span>
      <div className="flex gap-1.5">
        {blocks.map((b) => (
          <div key={b.label} className="flex flex-col items-center">
            <span className="bg-destructive/10 border border-destructive/20 text-destructive font-serif text-sm font-bold px-2 py-1 rounded-sm min-w-[32px] text-center tabular-nums">
              {String(b.value).padStart(2, "0")}
            </span>
            <span className="font-body text-[9px] text-muted-foreground mt-0.5">{b.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CountdownTimer;
